// Shared dark/light "console" kit — Palantir-style surface for the Analytics
// consoles. Theme provided via context by <ConsoleShell>, persisted in the same
// localStorage key as Command Center ('sanoire_cc_theme'). Includes interactive
// primitives: sortable/searchable/paginated Table, hover tooltips on Bar,
// slide-over Drawer, CSV export, and KPI deltas.
import React, { createContext, useContext, useState, useEffect } from 'react';

export const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';

// Sanoire Paper — canonical 3-stop substrate (brand-locked). Light mode only.
export const SANOIRE_PAPER = 'linear-gradient(180deg, #E8EEF2 0%, #F2EEE7 50%, #EDE8E0 100%)';

// Serif display face for mastheads, panel titles and hero numerals.
export const SERIF = 'Georgia,"Times New Roman",serif';

// ── Ink & Paper — the single committed admin theme (June 2026) ──────────────
// Paper canvas, white panels, ink masthead + ink KPI cards, gold hairlines.
// Both keys point at the same palette so existing THEMES.dark/.light reads work.
const INK_PAPER = {
  bg:'#F2EEE7', panel:'#ffffff', top:'#ffffff', kbg:'#ffffff', bd:'#e4ded2',
  tx:'#15242e', mut:'#6a8494', seal:'#314F5C', sealc:'#ffffff', period:'#f5f1e8',
  gbar:'#EDE8E0', accent:'#314F5C', up:'#17a86a', search:'#2563eb', warn:'#d97706',
  danger:'#dc3545', alert:'#fdf4e3', alertc:'#7c4a0a', alertbd:'#ecd9a8',
  codebg:'#f1ede3', live:'#eaf7f2', livec:'#0d6640', rowbg:'#faf8f2', tip:'#0f1e28',
  gold:'#9F7E4F', goldSoft:'#B99C6D', ink:'#112433', inkDeep:'#0d1d2b', inkTx:'#F2EEE7', inkMut:'#7fa0b4',
};
export const THEMES = { dark: INK_PAPER, light: INK_PAPER };

const Ctx = createContext({ mode:'dark', t:THEMES.dark, toggle:()=>{}, _inShell:false });
export const useConsoleTheme = () => useContext(Ctx);

export function useIsMobile(bp = 820) {
  const [m, setM] = useState(() => typeof window !== 'undefined' && window.innerWidth < bp);
  useEffect(() => {
    const h = () => setM(window.innerWidth < bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return m;
}

export function ConsoleShell({ title, right, children }) {
  const _parent = useContext(Ctx);
  const _nested = !!(_parent && _parent._inShell);
  const _isMobile = useIsMobile();
  // Single committed Ink & Paper theme — no light/dark toggle.
  const t = THEMES.light;
  const toggle = () => {};
  // Nested inside another ConsoleShell (e.g. a screen rendered as a hub tab):
  // inherit the parent's theme + chrome, don't paint a second eyebrow/shell.
  if (_nested) return <>{children}</>;

  return (
    <Ctx.Provider value={{ mode: 'light', t, toggle, _inShell:true }}>
      <div style={{ background: SANOIRE_PAPER, borderRadius:0, overflow:'hidden', fontFamily:F, width:'100%', minHeight:'100%' }}>
        <style>{`@keyframes cc-shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}@keyframes cc-fadeup{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}.cc-tbl tbody tr{transition:background .12s}.cc-tbl tbody tr:hover{background:var(--cc-row)}`}</style>
        {!_isMobile && right && (
          <div style={{ display:'flex', justifyContent:'flex-end', alignItems:'center', gap:8, padding:'14px 20px 0' }}>{right}</div>
        )}
        <div style={{ padding:'16px 20px 22px' }}>{children}</div>
      </div>
    </Ctx.Provider>
  );
}

export function KPI({ label, value, sub, subColor, accent, delta, onClick }) {
  const { t } = useConsoleTheme();
  const [h, setH] = useState(false);
  const dv = (delta == null || isNaN(delta)) ? null : Math.round(delta);
  const dColor = dv == null ? t.inkMut : dv > 0 ? '#5dcaa5' : dv < 0 ? '#e8736b' : t.inkMut;
  return (
    <div onClick={onClick} onMouseEnter={()=>setH(true)} onMouseLeave={()=>setH(false)}
      style={{ background:t.ink, border:'1px solid rgba(185,156,109,0.22)', borderRadius:10, padding:'15px 17px', position:'relative', overflow:'hidden', cursor:onClick?'pointer':'default', transform: h && onClick ? 'translateY(-2px)' : 'none', boxShadow: h && onClick ? '0 8px 20px rgba(13,29,43,0.35)' : '0 2px 8px rgba(13,29,43,0.18)', transition:'transform .15s, box-shadow .15s', animation:'cc-fadeup .35s ease both' }}>
      {accent && <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:accent }} />}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:7, gap:6 }}>
        <span style={{ fontSize:'0.78rem', fontWeight:600, color:t.inkMut, letterSpacing:'.14em', textTransform:'uppercase' }}>{label}</span>
        {dv != null && <span style={{ fontSize:'0.9rem', fontWeight:600, color:dColor, whiteSpace:'nowrap' }}>{dv > 0 ? '▲' : dv < 0 ? '▼' : '·'} {Math.abs(dv)}%</span>}
      </div>
      <div style={{ fontSize:'2.05rem', fontWeight:500, color:t.inkTx, lineHeight:1.05, fontFamily:SERIF, fontVariantNumeric:'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize:'0.95rem', fontFamily:SERIF, fontStyle:'italic', color:subColor||t.goldSoft, marginTop:5 }}>{sub}</div>}
    </div>
  );
}

export function Panel({ title, right, children, style }) {
  const { t } = useConsoleTheme();
  return (
    <div style={{ background:t.panel, border:`1px solid ${t.bd}`, borderRadius:11, padding:'16px 19px', animation:'cc-fadeup .35s ease both', ...style }}>
      {(title || right) && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:13, borderBottom:'1px solid rgba(159,126,79,0.28)', paddingBottom:9 }}>
          <span style={{ fontSize:'1.12rem', fontWeight:500, color:t.tx, fontFamily:SERIF }}>{title}</span>
          {right && <span style={{ fontSize:'0.95rem', color:t.mut }}>{right}</span>}
        </div>
      )}
      {children}
    </div>
  );
}

export function SectionLabel({ children, right }) {
  const { t } = useConsoleTheme();
  return (
    <div style={{ display:'flex', alignItems:'center', gap:12, margin:'18px 2px 10px' }}>
      <span style={{ fontSize:'0.82rem', fontWeight:600, color:t.gold, textTransform:'uppercase', letterSpacing:'.18em', whiteSpace:'nowrap' }}>{children}</span>
      <div style={{ flex:1, height:1, background:'rgba(159,126,79,0.28)' }} />
      {right && <span style={{ fontSize:'0.88rem', color:t.mut }}>{right}</span>}
    </div>
  );
}

export function Bar({ label, value, pct, color, labelWidth = 120, onClick, hint }) {
  const { t } = useConsoleTheme();
  const [h, setH] = useState(false);
  return (
    <div onClick={onClick} onMouseEnter={()=>setH(true)} onMouseLeave={()=>setH(false)}
      style={{ display:'flex', alignItems:'center', gap:9, marginBottom:8, fontSize:'1.02rem', position:'relative', cursor:onClick?'pointer':'default', background:h?t.rowbg:'transparent', borderRadius:6, padding:'2px 4px', transition:'background .12s' }}>
      <span style={{ width:labelWidth, flexShrink:0, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', color:t.tx }}>{label}</span>
      <div style={{ flex:1, height:18, borderRadius:4, overflow:'hidden', background:t.gbar }}>
        <div style={{ height:'100%', width:`${Math.min(100,Math.max(0,pct||0))}%`, borderRadius:4, background:color||t.accent, opacity:color?1:.5, transition:'width .4s' }} />
      </div>
      <span style={{ minWidth:52, textAlign:'right', flexShrink:0, fontVariantNumeric:'tabular-nums', color:t.mut }}>{value}</span>
      {h && (
        <div style={{ position:'absolute', right:60, bottom:'100%', marginBottom:4, background:t.tip, color:'#fff', fontSize:'0.9rem', padding:'4px 8px', borderRadius:6, whiteSpace:'nowrap', zIndex:20, pointerEvents:'none', boxShadow:'0 4px 12px rgba(0,0,0,.3)' }}>
          {hint || `${label}: ${value}`}
        </div>
      )}
    </div>
  );
}

export function StatLine({ label, value, color }) {
  const { t } = useConsoleTheme();
  return (
    <div style={{ display:'flex', justifyContent:'space-between', fontSize:'1.05rem', padding:'4px 0' }}>
      <span style={{ color:t.mut }}>{label}</span>
      <b style={{ color:color||t.tx, fontVariantNumeric:'tabular-nums', fontWeight:500 }}>{value}</b>
    </div>
  );
}

export function Empty({ children }) {
  const { t } = useConsoleTheme();
  return <div style={{ fontSize:'1.02rem', color:t.mut, padding:'10px 12px', background:t.rowbg, border:`1px solid ${t.bd}`, borderRadius:8, lineHeight:1.5, animation:'cc-fadeup .35s ease both' }}>{children}</div>;
}

export const PALETTE = ['#6FBFE0', '#5dcaa5', '#85b7eb', '#f0b357', '#9F7E4F', '#b48ce0', '#e8736b', '#7fa0b4'];

export function Gauge({ value, label, size = 132, color }) {
  const { t } = useConsoleTheme();
  const v = Math.max(0, Math.min(100, value || 0));
  const cx = size/2, r = cx - 11, cy = cx;
  const th = Math.PI * v/100;
  const x = (cx - r*Math.cos(th)).toFixed(2), y = (cy - r*Math.sin(th)).toFixed(2);
  const large = 0; // value arc spans <=180deg, always the minor arc
  const col = color || (v >= 80 ? t.up : v >= 40 ? t.warn : t.danger);
  return (
    <div style={{ textAlign:'center' }}>
      <svg width="100%" viewBox={`0 0 ${size} ${cy + 14}`} style={{ maxWidth:size }}>
        <path d={`M 11 ${cy} A ${r} ${r} 0 1 1 ${size-11} ${cy}`} fill="none" stroke={t.gbar} strokeWidth="10" strokeLinecap="round" />
        <path d={`M 11 ${cy} A ${r} ${r} 0 ${large} 1 ${x} ${y}`} fill="none" stroke={col} strokeWidth="10" strokeLinecap="round" style={{ transition:'all .5s' }} />
        <text x={cx} y={cy-6} textAnchor="middle" fontSize={size*0.2} fontWeight="500" fill={t.tx} fontFamily={F}>{Math.round(v)}%</text>
      </svg>
      <div style={{ fontSize:'0.82rem', color:t.mut, marginTop:-2 }}>{label}</div>
    </div>
  );
}

export function Donut({ data, size = 168, thickness = 26, center }) {
  const { t } = useConsoleTheme();
  const [hi, setHi] = useState(null);
  const total = (data || []).reduce((a, d) => a + (d.value || 0), 0) || 1;
  const r = size/2 - thickness/2, cx = size/2, cy = size/2;
  let acc = 0;
  const segs = (data || []).map((d, i) => {
    const frac = (d.value || 0) / total;
    const a0 = acc * 2*Math.PI - Math.PI/2; acc += frac;
    const a1 = acc * 2*Math.PI - Math.PI/2;
    const large = frac > 0.5 ? 1 : 0;
    const x0 = cx + r*Math.cos(a0), y0 = cy + r*Math.sin(a0);
    const x1 = cx + r*Math.cos(a1), y1 = cy + r*Math.sin(a1);
    return { d, i, frac, path:`M ${x0.toFixed(2)} ${y0.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${x1.toFixed(2)} ${y1.toFixed(2)}` };
  });
  return (
    <div style={{ display:'flex', alignItems:'center', gap:20, flexWrap:'wrap' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink:0 }}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={t.gbar} strokeWidth={thickness} />
        {segs.map(s => (
          <path key={s.i} d={s.path} fill="none" stroke={s.d.color} strokeWidth={hi===s.i?thickness+6:thickness}
            opacity={hi==null||hi===s.i?1:0.4} style={{ transition:'stroke-width .12s, opacity .12s', cursor:'default' }}
            onMouseEnter={()=>setHi(s.i)} onMouseLeave={()=>setHi(null)} />
        ))}
        <text x={cx} y={cy-2} textAnchor="middle" fontSize={size*0.15} fontWeight="500" fill={t.tx} fontFamily={F}>{hi!=null ? Math.round(segs[hi].frac*100)+'%' : (center ? center.value : '')}</text>
        <text x={cx} y={cy+size*0.13} textAnchor="middle" fontSize={size*0.065} fill={t.mut} fontFamily={F}>{hi!=null ? data[hi].label : (center ? center.label : '')}</text>
      </svg>
      <div style={{ flex:1, minWidth:140 }}>
        {(data || []).map((d, i) => (
          <div key={i} onMouseEnter={()=>setHi(i)} onMouseLeave={()=>setHi(null)} style={{ display:'flex', alignItems:'center', gap:8, fontSize:'0.85rem', padding:'4px 0', opacity:hi==null||hi===i?1:0.5 }}>
            <span style={{ width:11, height:11, borderRadius:3, background:d.color, flexShrink:0 }} />
            <span style={{ flex:1, color:t.tx, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{d.label}</span>
            <span style={{ color:t.mut, fontVariantNumeric:'tabular-nums' }}>{Math.round((d.value||0)/total*100)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Skeleton({ h = 16, w = '100%', mb = 8, r = 6 }) {
  const { t } = useConsoleTheme();
  return <div style={{ height:h, width:w, marginBottom:mb, borderRadius:r, background:`linear-gradient(90deg, ${t.gbar} 25%, ${t.bd} 50%, ${t.gbar} 75%)`, backgroundSize:'200% 100%', animation:'cc-shimmer 1.4s ease infinite' }} />;
}

export function SkeletonRows({ n = 4 }) {
  return <div>{Array.from({ length:n }).map((_, i) => <Skeleton key={i} w={`${90 - i * 8}%`} />)}</div>;
}

export function Freshness({ at }) {
  const { t } = useConsoleTheme();
  if (!at) return null;
  const mins = Math.max(0, Math.round((Date.now() - new Date(at).getTime()) / 60000));
  const txt = mins < 1 ? 'just now' : mins < 60 ? `${mins}m ago` : `${Math.round(mins / 60)}h ago`;
  return <span style={{ fontSize:'0.78rem', color:t.mut }}>updated {txt}</span>;
}

export function Pill({ children, tone }) {
  const { t } = useConsoleTheme();
  const c = tone==='up'?t.up : tone==='danger'?t.danger : tone==='warn'?t.warn : t.mut;
  return <span style={{ fontSize:'0.85rem', padding:'1px 7px', borderRadius:8, background:c+'22', color:c, fontWeight:600 }}>{children}</span>;
}

function cellText(col, row) {
  if (col.text) return String(col.text(row) ?? '');
  if (col.key) return String(row[col.key] ?? '');
  return '';
}
function cellSort(col, row) {
  if (col.sort) return col.sort(row);
  if (col.key) return row[col.key];
  return cellText(col, row);
}
export function toCsv(cols, rows) {
  const esc = s => '"' + String(s ?? '').replace(/"/g, '""') + '"';
  const head = cols.map(c => esc(c.label)).join(',');
  const body = rows.map(r => cols.map(c => esc(cellText(c, r))).join(',')).join('\n');
  return head + '\n' + body;
}
export function ExportButton({ filename, cols, rows }) {
  const { t } = useConsoleTheme();
  const onClick = () => {
    try {
      const csv = toCsv(cols, rows || []);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = (filename || 'export') + '.csv';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (_e) {}
  };
  return (
    <button onClick={onClick} style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'5px 11px', fontSize:'1rem', borderRadius:7, cursor:'pointer', border:`1px solid ${t.bd}`, background:t.period, color:t.tx, fontFamily:F, fontWeight:500 }}>
      ↓ CSV
    </button>
  );
}

export function Table({ cols, rows, empty, pageSize = 15, searchable, onRowClick }) {
  const { t } = useConsoleTheme();
  const [query, setQuery] = useState('');
  const [sortIdx, setSortIdx] = useState(-1);
  const [dir, setDir] = useState(1);
  const [page, setPage] = useState(0);
  const all = rows || [];
  const showSearch = searchable != null ? searchable : all.length > 8;

  let view = all;
  if (query.trim()) {
    const q = query.toLowerCase();
    view = view.filter(r => cols.some(c => cellText(c, r).toLowerCase().includes(q)));
  }
  if (sortIdx >= 0 && cols[sortIdx]) {
    const col = cols[sortIdx];
    view = view.slice().sort((a, b) => {
      const va = cellSort(col, a), vb = cellSort(col, b);
      if (va == null) return 1; if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }
  const pages = Math.max(1, Math.ceil(view.length / pageSize));
  const pg = Math.min(page, pages - 1);
  const slice = view.slice(pg * pageSize, pg * pageSize + pageSize);

  if (all.length === 0) return <Empty>{empty || 'No data yet.'}</Empty>;

  const headClick = i => { if (!(cols[i].sort || cols[i].key)) return; if (sortIdx === i) setDir(d => -d); else { setSortIdx(i); setDir(1); } };

  return (
    <div>
      {showSearch && (
        <input value={query} onChange={e => { setQuery(e.target.value); setPage(0); }} placeholder="Filter…"
          style={{ width:'100%', boxSizing:'border-box', marginBottom:10, fontSize:'1.02rem', padding:'6px 10px', borderRadius:7, border:`1px solid ${t.bd}`, background:t.rowbg, color:t.tx, outline:'none', fontFamily:F }} />
      )}
      <div style={{ overflowX:'auto' }}>
        <table className="cc-tbl" style={{ '--cc-row':t.rowbg, width:'100%', borderCollapse:'collapse', fontSize:'1rem' }}>
          <thead>
            <tr style={{ borderBottom:`1px solid ${t.bd}` }}>
              {cols.map((c, i) => {
                const sortable = !!(c.sort || c.key);
                const arrow = sortIdx === i ? (dir === 1 ? ' ▲' : ' ▼') : '';
                return (
                  <th key={i} onClick={() => headClick(i)} style={{ textAlign:c.align||'left', padding:'7px 8px', color:t.mut, fontWeight:500, whiteSpace:'nowrap', position:'sticky', top:0, background:t.panel, cursor:sortable?'pointer':'default', userSelect:'none' }}>
                    {c.label}{arrow}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {slice.map((r, ri) => (
              <tr key={ri} onClick={() => onRowClick && onRowClick(r)} style={{ borderBottom:`.5px solid ${t.bd}`, cursor:onRowClick?'pointer':'default' }}>
                {cols.map((c, ci) => (
                  <td key={ci} style={{ padding:'6px 8px', textAlign:c.align||'left', color:c.muted?t.mut:t.tx, fontFamily:c.mono?'monospace':F, fontVariantNumeric:c.num?'tabular-nums':'normal', whiteSpace:'nowrap' }}>
                    {typeof c.render === 'function' ? c.render(r) : r[c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {view.length > pageSize && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:10, fontSize:'0.93rem', color:t.mut }}>
          <span>{view.length.toLocaleString()} rows</span>
          <span style={{ display:'flex', alignItems:'center', gap:8 }}>
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={pg === 0} style={{ padding:'3px 8px', borderRadius:6, border:`1px solid ${t.bd}`, background:t.period, color:t.tx, cursor:pg===0?'default':'pointer', opacity:pg===0?.4:1 }}>‹</button>
            <span>{pg + 1} / {pages}</span>
            <button onClick={() => setPage(p => Math.min(pages - 1, p + 1))} disabled={pg >= pages - 1} style={{ padding:'3px 8px', borderRadius:6, border:`1px solid ${t.bd}`, background:t.period, color:t.tx, cursor:pg>=pages-1?'default':'pointer', opacity:pg>=pages-1?.4:1 }}>›</button>
          </span>
        </div>
      )}
    </div>
  );
}

export function Drawer({ open, onClose, title, children, width = 380 }) {
  const { t } = useConsoleTheme();
  if (!open) return null;
  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:9000, background:'rgba(5,12,20,0.5)', display:'flex', justifyContent:'flex-end' }}>
      <div onClick={e => e.stopPropagation()} style={{ width, maxWidth:'94vw', height:'100%', overflowY:'auto', background:t.panel, borderLeft:`1px solid ${t.bd}`, fontFamily:F, boxShadow:'-8px 0 28px rgba(0,0,0,.3)' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 16px', borderBottom:`1px solid ${t.bd}`, background:t.top, position:'sticky', top:0 }}>
          <span style={{ fontSize:'1.17rem', fontWeight:500, color:t.tx }}>{title}</span>
          <button onClick={onClose} style={{ width:26, height:26, borderRadius:6, border:`1px solid ${t.bd}`, background:t.period, color:t.tx, cursor:'pointer', fontSize:'1.23rem', lineHeight:1 }}>✕</button>
        </div>
        <div style={{ padding:16 }}>{children}</div>
      </div>
    </div>
  );
}

// ── Heatmap ───────────────────────────────────────────────────────────────
// hour × weekday intensity grid. cells = [{wd,hr,n}], max = peak cell count.
export function Heatmap({ cells, max, weekdayLabels, caption }) {
  const { t } = useConsoleTheme();
  const [hov, setHov] = useState(null);
  const days = weekdayLabels || ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const grid = {};
  (cells || []).forEach(c => { grid[c.wd + ':' + c.hr] = c.n; });
  const mx = max || Math.max(1, ...(cells || []).map(c => c.n || 0));
  const total = (cells || []).reduce((a, c) => a + (c.n || 0), 0);
  if (total === 0) return <Empty>No activity recorded yet — the grid fills in once Sana and search traffic arrive.</Empty>;
  const hh = h => String(h).padStart(2, '0') + ':00';
  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8, minHeight:18 }}>
        <span style={{ fontSize:'0.92rem', color:t.mut }}>{caption || 'Events by weekday × hour (UTC)'}</span>
        <span style={{ fontSize:'0.92rem', color:hov ? t.tx : t.mut, fontVariantNumeric:'tabular-nums' }}>
          {hov ? `${days[hov.wd]} ${hh(hov.hr)} — ${(hov.n || 0).toLocaleString()} event${hov.n === 1 ? '' : 's'}` : `${total.toLocaleString()} events · peak ${mx.toLocaleString()}`}
        </span>
      </div>
      <div style={{ overflowX:'auto' }}>
        <div style={{ display:'grid', gridTemplateColumns:`30px repeat(24, minmax(11px, 1fr))`, gap:3, minWidth:560 }}>
          <div />
          {Array.from({ length:24 }).map((_, h) => (
            <div key={'h'+h} style={{ fontSize:'0.62rem', color:t.mut, textAlign:'center', fontVariantNumeric:'tabular-nums' }}>{h % 3 === 0 ? h : ''}</div>
          ))}
          {days.map((dl, wd) => (
            <React.Fragment key={'r'+wd}>
              <div style={{ fontSize:'0.72rem', color:t.mut, display:'flex', alignItems:'center', height:17 }}>{dl}</div>
              {Array.from({ length:24 }).map((_, hr) => {
                const n = grid[wd + ':' + hr] || 0;
                const op = n ? 0.16 + 0.84 * (n / mx) : 1;
                const isHov = hov && hov.wd === wd && hov.hr === hr;
                return (
                  <div key={wd+'-'+hr}
                    onMouseEnter={() => setHov({ wd, hr, n })} onMouseLeave={() => setHov(null)}
                    title={`${dl} ${hh(hr)} · ${n}`}
                    style={{ height:17, borderRadius:3, background: n ? t.accent : t.gbar, opacity: op,
                      outline: isHov ? `1.5px solid ${t.tx}` : 'none', outlineOffset:-1, cursor:'default', transition:'opacity .1s' }} />
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:7, marginTop:10, fontSize:'0.74rem', color:t.mut }}>
        <span>less</span>
        {[0.16, 0.4, 0.64, 0.88, 1].map((o, i) => <span key={i} style={{ width:15, height:11, borderRadius:3, background: i === 0 ? t.gbar : t.accent, opacity: i === 0 ? 1 : o }} />)}
        <span>more</span>
      </div>
    </div>
  );
}

// ── StackedArea ───────────────────────────────────────────────────────────
// data = [{ <xKey>, ...metricKeys }], keys = [{ key, label, color }].
export function StackedArea({ data, keys, height = 210, xKey = 'day' }) {
  const { t } = useConsoleTheme();
  const [idx, setIdx] = useState(null);
  const wrapRef = React.useRef(null);
  const pts = data || [];
  const ks = keys || [];
  if (pts.length === 0 || ks.length === 0) return <Empty>No trend data yet.</Empty>;
  const grand = pts.reduce((a, p) => a + ks.reduce((b, k) => b + (+p[k.key] || 0), 0), 0);
  if (grand === 0) return <Empty>No activity in this window yet — the trend draws itself once events arrive.</Empty>;
  const W = 760, H = height, padL = 8, padR = 8, padT = 12, padB = 22;
  const innerW = W - padL - padR, innerH = H - padT - padB;
  const totals = pts.map(p => ks.reduce((a, k) => a + (+p[k.key] || 0), 0));
  const maxY = Math.max(1, ...totals);
  const xAt = i => padL + (pts.length === 1 ? innerW / 2 : (i / (pts.length - 1)) * innerW);
  const yAt = v => padT + innerH - (v / maxY) * innerH;
  let prev = pts.map(() => 0);
  const layers = ks.map(k => {
    const lower = prev.slice();
    const upper = pts.map((p, i) => lower[i] + (+p[k.key] || 0));
    prev = upper;
    const top = upper.map((v, i) => `${xAt(i).toFixed(1)},${yAt(v).toFixed(1)}`);
    const bot = lower.map((v, i) => `${xAt(i).toFixed(1)},${yAt(v).toFixed(1)}`).reverse();
    return { k, d: `M${top.join(' L')} L${bot.join(' L')} Z` };
  });
  const shortDate = s => { try { const d = new Date(s); return (d.getMonth() + 1) + '/' + d.getDate(); } catch (_e) { return s; } };
  const onMove = e => {
    const el = wrapRef.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const rel = (e.clientX - r.left) / r.width * W;
    const i = Math.round((rel - padL) / innerW * (pts.length - 1));
    setIdx(Math.max(0, Math.min(pts.length - 1, i)));
  };
  const hov = idx != null ? pts[idx] : null;
  return (
    <div ref={wrapRef} style={{ position:'relative' }} onMouseMove={onMove} onMouseLeave={() => setIdx(null)}>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display:'block' }}>
        {[0.25, 0.5, 0.75].map(g => <line key={g} x1={padL} x2={W - padR} y1={padT + innerH * g} y2={padT + innerH * g} stroke={t.bd} strokeWidth="1" />)}
        {layers.map((l, i) => <path key={i} d={l.d} fill={l.k.color} opacity={idx == null ? 0.86 : 0.96} stroke={l.k.color} strokeWidth="0.5" />)}
        {idx != null && <line x1={xAt(idx)} x2={xAt(idx)} y1={padT} y2={padT + innerH} stroke={t.tx} strokeWidth="1" strokeDasharray="3 3" opacity="0.6" />}
        <text x={padL} y={H - 6} fontSize="11" fill={t.mut} fontFamily={F}>{shortDate(pts[0][xKey])}</text>
        <text x={W / 2} y={H - 6} fontSize="11" fill={t.mut} fontFamily={F} textAnchor="middle">{shortDate(pts[Math.floor(pts.length / 2)][xKey])}</text>
        <text x={W - padR} y={H - 6} fontSize="11" fill={t.mut} fontFamily={F} textAnchor="end">{shortDate(pts[pts.length - 1][xKey])}</text>
      </svg>
      <div style={{ display:'flex', flexWrap:'wrap', gap:14, marginTop:6 }}>
        {ks.map(k => (
          <span key={k.key} style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:'0.9rem', color:t.mut }}>
            <span style={{ width:11, height:11, borderRadius:3, background:k.color }} />{k.label}
            {hov && <b style={{ color:t.tx, fontVariantNumeric:'tabular-nums' }}>{(+hov[k.key] || 0).toLocaleString()}</b>}
          </span>
        ))}
      </div>
      {hov && (
        <div style={{ position:'absolute', top:0, right:0, background:t.tip, color:'#fff', fontSize:'0.82rem', padding:'5px 9px', borderRadius:7, pointerEvents:'none', boxShadow:'0 4px 12px rgba(0,0,0,.3)' }}>
          <div style={{ opacity:0.8, marginBottom:2 }}>{(() => { try { return new Date(hov[xKey]).toLocaleDateString(undefined, { month:'short', day:'numeric' }); } catch (_e) { return hov[xKey]; } })()}</div>
          <b style={{ fontVariantNumeric:'tabular-nums' }}>{(totals[idx] || 0).toLocaleString()} total</b>
        </div>
      )}
    </div>
  );
}

// ── Funnel ────────────────────────────────────────────────────────────────
// steps = [{ step, count }]. Renders centred tapering bars + conversion deltas.
export function Funnel({ steps, color }) {
  const { t } = useConsoleTheme();
  const [hi, setHi] = useState(null);
  const s = steps || [];
  if (s.length === 0) return <Empty>No funnel data yet.</Empty>;
  const max = Math.max(1, ...s.map(x => x.count || 0));
  const top = s[0] && s[0].count ? s[0].count : 0;
  return (
    <div>
      {s.map((st, i) => {
        const c = st.count || 0;
        const pct = Math.max(2, (c / max) * 100);
        const prev = i > 0 ? (s[i - 1].count || 0) : null;
        const conv = prev != null ? (prev ? Math.round((c / prev) * 100) : 0) : null;
        const ofTop = top ? Math.round((c / top) * 100) : 0;
        const col = color || PALETTE[i % PALETTE.length];
        return (
          <div key={i} onMouseEnter={() => setHi(i)} onMouseLeave={() => setHi(null)} style={{ marginBottom:10 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:4, fontSize:'1rem' }}>
              <span style={{ color:t.tx }}>{st.step}</span>
              <span style={{ color:t.mut, fontVariantNumeric:'tabular-nums' }}>
                {c.toLocaleString()}{conv != null && <span style={{ color: conv >= 50 ? t.up : conv > 0 ? t.warn : t.danger, marginLeft:8 }}>{conv}%↓</span>}
              </span>
            </div>
            <div style={{ height:26, borderRadius:6, background:t.gbar, overflow:'hidden', display:'flex', justifyContent:'center' }}>
              <div style={{ width:`${pct}%`, height:'100%', background:col, opacity: hi == null || hi === i ? 1 : 0.45, borderRadius:6, transition:'opacity .12s, width .4s', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <span style={{ fontSize:'0.78rem', color:'#fff', fontWeight:600, opacity: pct > 12 ? 1 : 0 }}>{ofTop}%</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
